import {announce} from '@github-ui/aria-live'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import type {IpmMetricTags} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {Icon} from '@primer/octicons-react'
import {CopilotIcon, MarkGithubIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Label, LinkButton} from '@primer/react'
import {Profiler, useEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import styles from './MergeBoxNudgeSection.module.css'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import type {MergeBoxNudge, MergeBoxNudgeIcon} from '../../page-data/payloads/merge-box'

export interface MergeBoxNudgeSectionProps {
  nudge: MergeBoxNudge
}

// An icon cannot cross the payload boundary as a component, so dotcom names one instead.
const CTA_ICONS: Record<MergeBoxNudgeIcon, Icon> = {
  'mark-github': MarkGithubIcon,
  copilot: CopilotIcon,
}

function analyticsEvent(nudge: MergeBoxNudge, action: string) {
  return {
    category: nudge.analyticsCategory,
    action,
    label: `location:${nudge.analyticsLocation};surface:${nudge.analyticsSurface};variant:${nudge.variant};`,
  }
}

/** The merge box's nudge slot: dotcom supplies every value, so this holds no copy, destination, or eligibility logic. */
export function MergeBoxNudgeSection({nudge}: MergeBoxNudgeSectionProps) {
  const [dismissed, setDismissed] = useState(false)
  const dismissedFocusRef = useRef<HTMLSpanElement>(null)

  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'MergeBoxNudgeSection',
      surface: nudge.analyticsSurface,
      display_location: nudge.analyticsLocation,
      category: nudge.analyticsCategory,
      nudge_id: nudge.nudgeId,
      variant: nudge.variant,
    }),
    [nudge.analyticsCategory, nudge.analyticsLocation, nudge.analyticsSurface, nudge.nudgeId, nudge.variant],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  // The Hydro half of the impression rides on `data-analytics-visible` below, which the
  // global instrumenter fires once the element is actually on screen.
  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  if (dismissed) {
    return <span ref={dismissedFocusRef} tabIndex={-1} className="sr-only" data-testid={`${nudge.testId}-dismissed`} />
  }

  const handleDismiss = async () => {
    sendEvent('analytics.click', analyticsEvent(nudge, 'dismiss'))
    reportIpmDismiss(telemetryTags)

    try {
      await verifiedFetch(nudge.dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // A failed dismissal still hides the nudge; the viewer asked for it gone.
    } finally {
      // Removing the section takes the focused dismiss button with it; flush so the sentinel
      // exists to catch focus before it falls to `document.body`.
      // eslint-disable-next-line @eslint-react/dom-no-flush-sync
      flushSync(() => setDismissed(true))

      const active = document.activeElement
      if (active === null || active === document.body) {
        dismissedFocusRef.current?.focus()
      }
      announce(nudge.dismissedAnnouncement)
    }
  }

  const CtaIcon = nudge.ctaIcon ? CTA_ICONS[nudge.ctaIcon] : undefined

  const section = (
    <div
      className={styles.Container}
      data-testid={nudge.testId}
      data-analytics-visible={JSON.stringify(analyticsEvent(nudge, 'visible'))}
    >
      <MergeBoxSectionHeader
        title={
          <span className={styles.TitleRow}>
            {nudge.title}
            {nudge.label ? <Label variant={nudge.labelVariant ?? 'default'}>{nudge.label}</Label> : null}
          </span>
        }
        subtitle={nudge.description}
        rightSideContent={
          <div className={styles.Actions}>
            <LinkButton
              href={nudge.ctaUrl}
              leadingVisual={CtaIcon}
              data-testid={`${nudge.testId}-cta`}
              onClick={() => {
                sendEvent('analytics.click', analyticsEvent(nudge, nudge.ctaAnalyticsAction))
                reportIpmAction('cta_click', telemetryTags)
              }}
            >
              {nudge.ctaLabel}
            </LinkButton>
            <IconButton
              icon={XIcon}
              variant="invisible"
              aria-label={nudge.dismissLabel}
              data-testid={`${nudge.testId}-dismiss`}
              onClick={handleDismiss}
            />
          </div>
        }
      />
    </div>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="MergeBoxNudgeSection" fallback={null}>
      {renderTiming.isEnabled ? (
        <Profiler id="MergeBoxNudgeSection" onRender={renderTiming.onRender}>
          {section}
        </Profiler>
      ) : (
        section
      )}
    </IpmErrorBoundary>
  )
}
